import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  CreateSizeSetRequest,
  SizeSet,
  UpdateSizeSetRequest,
} from '@nucleus-commerce/api-client/admin';

import { sizeSetsApi } from '../api/size-sets-api';

import { dictionaryKeys } from './dictionary-keys';

/** Writes the saved set into its detail entry and the list (ordered by name, as served). */
function remember(queryClient: ReturnType<typeof useQueryClient>, row: SizeSet) {
  queryClient.setQueryData(dictionaryKeys.sizeSet(row.id), row);
  queryClient.setQueryData<SizeSet[]>(dictionaryKeys.sizeSets(), (rows) => {
    if (!rows) return rows;
    const index = rows.findIndex((r) => r.id === row.id);
    const next = index === -1 ? [...rows, row] : rows.with(index, row);
    return next.sort((a, b) => a.name.localeCompare(b.name));
  });
  return queryClient.invalidateQueries({ queryKey: dictionaryKeys.sizeSets(), exact: true });
}

/** 409 ALREADY_EXISTS on `key`, 400 on duplicate values; the editor shows them on the fields. */
export function useCreateSizeSet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateSizeSetRequest) => sizeSetsApi.create(body),
    meta: { successMessage: 'sizeSets:toasts.created', silentError: true },
    onSuccess: (row) => remember(queryClient, row),
  });
}

export function useUpdateSizeSet(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateSizeSetRequest) => sizeSetsApi.update(id, body),
    meta: { successMessage: 'sizeSets:toasts.updated', silentError: true },
    onSuccess: (row) => remember(queryClient, row),
  });
}

/** 409 RESOURCE_IN_USE while a customization still draws its options from the set. */
export function useDeleteSizeSet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => sizeSetsApi.remove(id),
    meta: { successMessage: 'sizeSets:toasts.deleted', silentError: true },
    onSuccess: (_, id) => {
      queryClient.setQueryData<SizeSet[]>(dictionaryKeys.sizeSets(), (rows) =>
        rows?.filter((row) => row.id !== id),
      );
      queryClient.removeQueries({ queryKey: dictionaryKeys.sizeSet(id) });
      return queryClient.invalidateQueries({ queryKey: dictionaryKeys.sizeSets(), exact: true });
    },
  });
}
